import React from "react";
import { useTranslation } from "react-i18next";

interface LockedAnalyticsCardProps {
  title: string;
  icon: React.ReactNode;
}

/** Drop-in: same props, non-interactive, frosted row */
const LockedAnalyticsCard: React.FC<LockedAnalyticsCardProps> = ({ title, icon }) => {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t("common.lockedAnalyticsCard.lockedAnalyticsCard.text.lockedInsight", { title })}
      role="status"
      className={[
        "locked-analytics-card",
        // shape & clipping so blur stays inside the pill
        "w-full rounded-2xl overflow-hidden",
        // FROSTED GLASS
        // - translucent bg so we can see the blur
        // - backdrop blur & a bit of saturation for the glassy look
        // - thin white ring to make it read on bright backgrounds
        "bg-white backdrop-blur-md backdrop-saturate-150 ring-1 ring-white/60",
        // soft shadow
        // "shadow-[0_1px_0_rgba(255,255,255,0.6),0_8px_24px_rgba(35,42,63,0.06)]",
        // spacing & make it obviously non-clickable
        "px-3 py-2 select-text",
      ].join(" ")}
      data-name="Locked Analytics Row (Frosted)"
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: icon chip + title */}
        <div className="flex min-w-0 items-center gap-3">
          <div
            aria-hidden="true"
            className={[
              "grid h-8 w-8 place-items-center rounded-xl",
              // light glass behind the icon so it looks like your screenshot
              "bg-white/40 backdrop-blur-sm ring-1 ring-white/60 text-[#5A88FF]",
              // Add blur to the icon container
              "blur-[1.5px]", // Adjust blur value as needed
            ].join(" ")}
          >
            {icon}
          </div>

          <h3 className="truncate text-[15px] font-semibold text-[#6b7a99] blur-[1.25px]">
            {title}
          </h3>
        </div>

        {/* Right: lock + helper (NOT clickable) */}
        <div className="flex cursor-default flex-col items-end leading-tight">
          <div className="flex items-center gap-1 text-sm font-medium text-[#3B82F6]">
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 14"
              fill="currentColor"
              className="shrink-0"
              aria-hidden="true"
            >
              <path d="M10 6V4C10 1.79 8.21 0 6 0S2 1.79 2 4v2c-.55 0-1 .45-1 1v5c0 .55.45 1 1 1h8c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1ZM3.5 4C3.5 2.62 4.62 1.5 6 1.5S8.5 2.62 8.5 4V6H3.5V4Z" />
            </svg>
            <span>{t("common.lockedAnalyticsCard.lockedAnalyticsCard.text.unlockInsights")}</span>
          </div>
          <div className="text-xs text-[#4F77F6]">
            {t("common.lockedAnalyticsCard.lockedAnalyticsCard.text.connectCraveEnable")}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LockedAnalyticsCard;
